import { useEffect, useRef, type JSX } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * Pinned-camera card ("show your face", ADR-0030) — a larger, focused view of ONE nearby citizen's live
 * webcam, docked into the top-left media slot (where it makes the radio yield/collapse). Pinning is a
 * private, per-viewer choice: it changes only YOUR screen, the pinned person is never told who pinned
 * them, and there is no control over their camera here — it is a window, not a console. The feed is the
 * same proximity-gated, ephemeral stream as their head bubble; when they turn the camera off or walk out
 * of range the parent auto-unpins (this card never shows a frozen/stale face).
 *
 * Positioning is owned by the parent media-slot wrapper (so it can stack above the collapsed radio).
 */
export interface PinnedCameraCardProps {
  /** The pinned citizen's display name (resolved from the roster upstream). */
  name: string;
  /** Their live webcam stream, or null while it (re)connects — the card shows a calm placeholder then. */
  stream: MediaStream | null;
  /** Close the pin (the ✕). Same effect as clicking their head bubble again, or them turning the cam off. */
  onClose: () => void;
}

export function PinnedCameraCard({ name, stream, onClose }: PinnedCameraCardProps): JSX.Element {
  const reduceMotion = useReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Attach the live stream to the <video>. Muted + inline so it autoplays without sound — the AUDIO rides
  // the voice graph (flat, zone-gated), never this element. Re-runs when the stream identity flips.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = stream;
    if (stream) void el.play().catch(() => undefined);
    return () => {
      el.srcObject = null;
    };
  }, [stream]);

  return (
    <motion.section
      initial={reduceMotion ? false : { opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      aria-label={`${name}'s camera`}
      className="pointer-events-auto flex w-full flex-col items-center gap-2 rounded-2xl border border-white/10 bg-black/50 p-3 text-white shadow-2xl backdrop-blur-md"
    >
      <div className="flex w-full items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
          <span aria-hidden="true">📷</span>
          <span className="truncate">{name}</span>
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={`Close ${name}'s camera`}
          title="Close"
          className="-mr-1 shrink-0 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs font-medium text-white/80 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          <span aria-hidden="true">✕</span>
        </button>
      </div>

      {/* The live face — a round porthole echoing the in-world bubble. object-cover center-crops any aspect. */}
      <div className="relative aspect-square w-full overflow-hidden rounded-full border-2 border-amber-200/40 bg-black/60">
        <video ref={videoRef} muted autoPlay playsInline className="h-full w-full object-cover" />
        {!stream && (
          <span className="absolute inset-0 flex items-center justify-center text-xs text-white/40">
            connecting…
          </span>
        )}
      </div>

      {/* Reassures this is an in-world, proximity-scoped, ephemeral feed — not a recording, not a call. */}
      <p role="status" aria-live="polite" className="text-[11px] text-white/60">
        <span aria-hidden="true">📷</span> live · nearby
      </p>
    </motion.section>
  );
}
